/**
 * Cognito Hosted UI login via Authorization Code + PKCE, hand-rolled (no
 * aws-amplify/cognito-sdk dependency — the flow is small and self-contained).
 *
 * Session storage only (not localStorage): there's no refresh-token flow
 * here, so a token surviving past its ~1hr lifetime buys nothing — closing
 * the tab just means logging in again next time, a deliberate MVP trade-off.
 */

import { navigate } from "../lib/router";
import { generateCodeChallenge, generateCodeVerifier, generateState } from "./pkce";

const STORAGE_KEY = "averagejoesff.auth";
const VERIFIER_KEY = "averagejoesff.auth.verifier";
const STATE_KEY = "averagejoesff.auth.state";
const RETURN_PATH_KEY = "averagejoesff.auth.returnPath";

export interface Session {
  idToken: string;
  email: string | null;
  /** Unix seconds. */
  expiresAt: number;
}

function domain(): string {
  const d = import.meta.env.VITE_COGNITO_DOMAIN;
  if (!d) throw new Error("VITE_COGNITO_DOMAIN is not set");
  return d;
}

function clientId(): string {
  const id = import.meta.env.VITE_COGNITO_CLIENT_ID;
  if (!id) throw new Error("VITE_COGNITO_CLIENT_ID is not set");
  return id;
}

function redirectUri(): string {
  return `${window.location.origin}/`;
}

/** Decode a JWT payload without verifying it — verification is API Gateway's
 *  job on every write; here we only need `exp`/`email` to drive the UI. */
function decodeJwtPayload(token: string): Record<string, unknown> {
  const payload = token.split(".")[1] ?? "";
  const padded = payload.replace(/-/g, "+").replace(/_/g, "/").padEnd(
    payload.length + ((4 - (payload.length % 4)) % 4),
    "=",
  );
  return JSON.parse(atob(padded)) as Record<string, unknown>;
}

export function loadSession(): Session | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw) as Session;
    if (session.expiresAt * 1000 <= Date.now()) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

function saveSession(idToken: string): Session {
  const claims = decodeJwtPayload(idToken);
  const session: Session = {
    idToken,
    email: typeof claims.email === "string" ? claims.email : null,
    expiresAt: typeof claims.exp === "number" ? claims.exp : 0,
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  return session;
}

export async function login(): Promise<void> {
  const verifier = generateCodeVerifier();
  const state = generateState();
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  // Cognito's registered redirect_uri is fixed to the site root (below), so
  // the Hosted UI always lands the browser back on "/" regardless of which
  // page login was triggered from. Stash the page here and restore it in
  // handleRedirectCallback so a sign-in from e.g. /dues returns to /dues.
  sessionStorage.setItem(RETURN_PATH_KEY, window.location.pathname);

  const challenge = await generateCodeChallenge(verifier);
  const url = new URL(`https://${domain()}/oauth2/authorize`);
  url.search = new URLSearchParams({
    response_type: "code",
    client_id: clientId(),
    redirect_uri: redirectUri(),
    scope: "openid email",
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
  }).toString();

  window.location.assign(url.toString());
}

export function logout(): void {
  sessionStorage.removeItem(STORAGE_KEY);
  const url = new URL(`https://${domain()}/logout`);
  url.search = new URLSearchParams({
    client_id: clientId(),
    logout_uri: redirectUri(),
  }).toString();
  window.location.assign(url.toString());
}

/**
 * On app load: if the URL carries `?code=&state=` from a Hosted UI redirect,
 * verify `state`, exchange the code for tokens, persist the session, and
 * strip the query string. No-op if there's nothing to handle.
 */
export async function handleRedirectCallback(): Promise<Session | null> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const returnedState = params.get("state");
  if (!code || !returnedState) return null;

  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const returnPath = sessionStorage.getItem(RETURN_PATH_KEY) ?? "/";
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(RETURN_PATH_KEY);

  // Always strip the query string, even on failure, so a bad/replayed code
  // doesn't linger in the address bar or get retried on refresh — and land
  // back on whatever page login was triggered from (see `login` above),
  // since redirect_uri always brings us back to "/" itself.
  navigate(returnPath, { replace: true });

  if (!verifier || returnedState !== expectedState) {
    throw new Error("Login failed: state mismatch. Please try logging in again.");
  }

  const res = await fetch(`https://${domain()}/oauth2/token`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: clientId(),
      code,
      redirect_uri: redirectUri(),
      code_verifier: verifier,
    }),
  });

  if (!res.ok) {
    throw new Error(`Login failed (${res.status}): ${await res.text()}`);
  }

  const tokens = (await res.json()) as { id_token: string };
  return saveSession(tokens.id_token);
}
